import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Grid,
  TextField,
  MenuItem,
  Snackbar,
  Alert,
  Tooltip,
  Button,
  Checkbox,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Stack,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Divider,
  Chip,
  Paper,
  InputAdornment,
} from "@mui/material";
import {
  Add,
  Delete,
  Edit,
  Save,
  Close as CloseIcon,
} from "@mui/icons-material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import axios from "axios";
import config from "../../config";
import useLayoutStore from "../../store/useLayoutStore";

// DND-KIT
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const hideXs = { display: { xs: "none", sm: "table-cell" } };
const ellipsisCell = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const formatPrice = (v) => `${Number(v || 0).toLocaleString()}₫`;

const toSlug = (str) =>
  (str || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/gi, "d")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

// ✅ Row có thể kéo thả
function SortableRow({ row, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: row.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    background: isDragging ? "rgba(0,0,0,0.03)" : undefined,
  };

  return (
    <TableRow ref={setNodeRef} hover sx={style}>
      <TableCell sx={{ width: 44 }}>
        <IconButton
          size="small"
          {...attributes}
          {...listeners}
          sx={{ cursor: "grab" }}
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
      </TableCell>

      {children}
    </TableRow>
  );
}

export default function CollectionProducts() {
  const navigate = useNavigate();
  const { isSidebarOpen } = useLayoutStore(); // bạn đang dùng store này ở project
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down("sm"));

  const [collections, setCollections] = useState([]);
  const [collectionId, setCollectionId] = useState("");
  // ✅ Drop-only filters
  const [onlyDrop, setOnlyDrop] = useState(true);
  const [filterEra, setFilterEra] = useState("new"); // new | old | all
  const [filterActive, setFilterActive] = useState("true"); // "true" | "false" | "all"

  const [searchAll, setSearchAll] = useState("");
  const [searchIn, setSearchIn] = useState("");
  const [loadingLeft, setLoadingLeft] = useState(false);
  const [loadingRight, setLoadingRight] = useState(false);
  const [error, setError] = useState("");

  const [allProducts, setAllProducts] = useState([]);
  const [inCollection, setInCollection] = useState([]);

  const [selectedLeftIds, setSelectedLeftIds] = useState(new Set());
  const [toast, setToast] = useState({ open: false, msg: "", sev: "success" });

  // Dialog states
  const [openCreate, setOpenCreate] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newSlug, setNewSlug] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newSeason, setNewSeason] = useState("");
  const [newShortDesc, setNewShortDesc] = useState("");
  const [newHeroImageUrl, setNewHeroImageUrl] = useState("");
  const [newFeaturedImageUrl, setNewFeaturedImageUrl] = useState("");
  const [newStoryTitle, setNewStoryTitle] = useState("");
  const [newStoryBody, setNewStoryBody] = useState("");
  const [newEra, setNewEra] = useState("new"); // "new" | "old" | "all"
  const [newActive, setNewActive] = useState(true);
  const [newSortOrder, setNewSortOrder] = useState("");

  const showToast = useCallback((msg, sev = "success") => {
    setToast({ open: true, msg, sev });
  }, []);

  // ✅ DND sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Load collections
  // Load collections (Drop-only)
  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        setError("");

        // ✅ Drop only
        if (onlyDrop) {
          const res = await axios.get(
            `${config.apiBaseUrl}/collections/drops`,
            {
              params: {
                era: filterEra === "all" ? null : filterEra,
                isActive: filterActive === "all" ? null : filterActive === "true",
              },
              signal: controller.signal,
            },
          );

          const items = res?.data?.items || res?.data || [];
          setCollections(items);

          const firstId = items?.[0]?.id ?? items?.[0]?.collectionId;
          if (firstId && !collectionId) setCollectionId(firstId);

          // nếu collectionId hiện tại không nằm trong list -> reset
          if (
            collectionId &&
            !items.some((c) => (c.id ?? c.collectionId) === collectionId)
          ) {
            setCollectionId(firstId || "");
          }
          return;
        }

        // fallback: all collections (admin)
        const res = await axios.get(`${config.apiBaseUrl}/collections`, {
          params: { admin: true },
          signal: controller.signal,
        });
        const items = res?.data?.items || res?.data || [];
        setCollections(items);

        const firstId = items?.[0]?.id ?? items?.[0]?.collectionId;
        if (firstId && !collectionId) setCollectionId(firstId);
        if (
          collectionId &&
          !items.some((c) => (c.id ?? c.collectionId) === collectionId)
        ) {
          setCollectionId(firstId || "");
        }
      } catch (e) {
        if (!axios.isCancel(e))
          setError(
            e?.response?.data?.message ||
              "Không tải được danh sách bộ sưu tập.",
          );
      }
    })();

    return () => controller.abort();
    // eslint-disable-next-line
  }, [onlyDrop, filterEra, filterActive]);

  // Auto tạo slug
  useEffect(() => {
    if (!newSlug) setNewSlug(toSlug(newName));
    // eslint-disable-next-line
  }, [newName]);

  // Load all products
  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        setLoadingLeft(true);
        const res = await axios.get(`${config.apiBaseUrl}/Products`, {
          signal: controller.signal,
        });
        const data = res?.data || [];
        const mapped = data.map((item) => ({
          id: item.productId,
          name: item.name,
          sku: item.sku,
          basePrice: item.price ?? item.basePrice ?? 0,
          priceText: formatPrice(item.price ?? item.basePrice ?? 0),
          totalStock: item.totalStock ?? 0,
          stockStatus: (item.totalStock ?? 0) > 0 ? "Còn hàng" : "Hết hàng",
        }));
        setAllProducts(mapped);
      } catch (e) {
        if (!axios.isCancel(e))
          setError(
            e?.response?.data?.message || "Không tải được danh sách sản phẩm.",
          );
      } finally {
        setLoadingLeft(false);
      }
    })();
    return () => controller.abort();
  }, []);

  // Load products in collection
  useEffect(() => {
    if (!collectionId) return;
    const controller = new AbortController();
    (async () => {
      try {
        setLoadingRight(true);
        const res = await axios.get(
          `${config.apiBaseUrl}/collections/${collectionId}/products`,
          {
            params: { page: 1, pageSize: 999, sort: "order" },
            signal: controller.signal,
          },
        );
        const list = res?.data?.items || res?.data?.products || res?.data || [];
        const items = list.map((p, idx) => ({
          id: p.productId ?? p.id,
          name: p.name,
          sku: p.sku,
          basePrice: p.finalPrice ?? p.basePrice ?? 0,
          priceText: formatPrice(p.finalPrice ?? p.basePrice ?? 0),
          sortOrder: p.sortOrder ?? idx + 1,
          totalStock: p.totalStock ?? p.stock ?? 0,
          stockStatus:
            (p.totalStock ?? p.stock ?? 0) > 0 ? "Còn hàng" : "Hết hàng",
        }));
        items.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
        setInCollection(items);
      } catch (e) {
        if (!axios.isCancel(e))
          setError(
            e?.response?.data?.message ||
              "Không tải được sản phẩm trong bộ sưu tập.",
          );
      } finally {
        setLoadingRight(false);
      }
    })();
    return () => controller.abort();
  }, [collectionId]);

  // Filter left
  const filteredAll = useMemo(() => {
    const q = (searchAll || "").toLowerCase();
    return allProducts.filter(
      (p) =>
        p.name?.toLowerCase().includes(q) || p.sku?.toLowerCase().includes(q),
    );
  }, [allProducts, searchAll]);

  // Filter right (hiển thị) — NOTE: reorder vẫn dựa trên full inCollection
  const filteredIn = useMemo(() => {
    const q = (searchIn || "").toLowerCase();
    return inCollection.filter(
      (p) =>
        p.name?.toLowerCase().includes(q) || p.sku?.toLowerCase().includes(q),
    );
  }, [inCollection, searchIn]);

  // Multi-select left
  const toggleLeft = useCallback((id, checked) => {
    setSelectedLeftIds((prev) => {
      const next = new Set(prev);
      checked ? next.add(id) : next.delete(id);
      return next;
    });
  }, []);

  const toggleLeftAllFiltered = useCallback(
    (checked) => {
      setSelectedLeftIds((prev) => {
        const next = new Set(prev);
        filteredAll.forEach((r) =>
          checked ? next.add(r.id) : next.delete(r.id),
        );
        return next;
      });
    },
    [filteredAll],
  );

  const selectedCount = selectedLeftIds.size;
  const selectedAll =
    filteredAll.length > 0 &&
    filteredAll.every((r) => selectedLeftIds.has(r.id));
  const selectedSome =
    filteredAll.some((r) => selectedLeftIds.has(r.id)) && !selectedAll;

  // Add one
  const handleAddOne = useCallback(
    async (row) => {
      if (!collectionId)
        return showToast("Vui lòng chọn bộ sưu tập trước.", "warning");
      if (inCollection.some((x) => x.id === row.id))
        return showToast("Sản phẩm đã có trong bộ sưu tập.", "info");
      try {
        const sortOrder =
          (inCollection[inCollection.length - 1]?.sortOrder ?? 0) + 1;
        await axios.post(
          `${config.apiBaseUrl}/collections/${collectionId}/products`,
          [{ productId: row.id, sortOrder }],
        );
        setInCollection((prev) => [
          ...prev,
          {
            id: row.id,
            name: row.name,
            sku: row.sku,
            basePrice: row.basePrice,
            priceText: row.priceText,
            sortOrder,
            totalStock: row.totalStock,
            stockStatus: row.stockStatus,
          },
        ]);
        showToast("Đã thêm vào bộ sưu tập.");
      } catch (e) {
        showToast(e?.response?.data?.message || "Thêm thất bại.", "error");
      }
    },
    [collectionId, inCollection, showToast],
  );

  // Add many
  const handleAddMany = useCallback(async () => {
    if (!collectionId)
      return showToast("Vui lòng chọn bộ sưu tập trước.", "warning");
    const existing = new Set(inCollection.map((x) => x.id));
    const idsToAdd = [...selectedLeftIds].filter((id) => !existing.has(id));
    if (idsToAdd.length === 0)
      return showToast("Tất cả sản phẩm đã có trong bộ sưu tập.", "info");

    try {
      const startOrder =
        (inCollection[inCollection.length - 1]?.sortOrder ?? 0) + 1;
      const payload = idsToAdd.map((id, i) => ({
        productId: id,
        sortOrder: startOrder + i,
      }));

      await axios.post(
        `${config.apiBaseUrl}/collections/${collectionId}/products`,
        payload,
      );

      const mapById = new Map(allProducts.map((p) => [p.id, p]));
      setInCollection((prev) => [
        ...prev,
        ...payload.map((p, i) => {
          const src = mapById.get(p.productId);
          return {
            id: p.productId,
            name: src?.name ?? "",
            sku: src?.sku ?? "",
            basePrice: src?.basePrice ?? 0,
            priceText: src ? src.priceText : formatPrice(0),
            sortOrder: startOrder + i,
            totalStock: src?.totalStock ?? 0,
            stockStatus: src?.stockStatus ?? "Hết hàng",
          };
        }),
      ]);

      setSelectedLeftIds((prev) => {
        const next = new Set(prev);
        idsToAdd.forEach((id) => next.delete(id));
        return next;
      });

      showToast(`Đã thêm ${idsToAdd.length} sản phẩm.`);
    } catch (e) {
      showToast(e?.response?.data?.message || "Thêm thất bại.", "error");
    }
  }, [collectionId, inCollection, selectedLeftIds, allProducts, showToast]);

  const handleRemoveFromCollection = useCallback(
    async (row) => {
      try {
        await axios.delete(
          `${config.apiBaseUrl}/collections/${collectionId}/products/${row.id}`,
        );
        setInCollection((prev) =>
          prev
            .filter((p) => p.id !== row.id)
            .map((x, i) => ({ ...x, sortOrder: i + 1 })),
        );
        showToast("Đã xoá khỏi bộ sưu tập.");
      } catch (e) {
        showToast(e?.response?.data?.message || "Xoá thất bại.", "error");
      }
    },
    [collectionId, showToast],
  );

  // ✅ Drag end reorder (chỉ reorder khi KHÔNG search để tránh reorder list filter)
  const handleDragEnd = useCallback(
    (event) => {
      if (searchIn) return; // đang search thì không cho reorder để tránh khó hiểu
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      setInCollection((prev) => {
        const oldIndex = prev.findIndex((x) => x.id === active.id);
        const newIndex = prev.findIndex((x) => x.id === over.id);
        const moved = arrayMove(prev, oldIndex, newIndex);
        return moved.map((x, i) => ({ ...x, sortOrder: i + 1 }));
      });
    },
    [searchIn],
  );

  const saveReorder = useCallback(async () => {
    try {
      const orders = inCollection.map((x) => ({
        productId: x.id,
        sortOrder: x.sortOrder,
      }));
      await axios.patch(
        `${config.apiBaseUrl}/collections/${collectionId}/reorder`,
        orders,
      );
      showToast("Đã lưu sắp xếp.");
    } catch (e) {
      showToast(e?.response?.data?.message || "Lưu sắp xếp thất bại.", "error");
    }
  }, [collectionId, inCollection, showToast]);

  // Create/Edit collection
  const resetForm = () => {
    setNewName("");
    setNewSlug("");
    setNewDescription("");
    setNewEra("new");
    setNewActive(true);
    setNewSortOrder("");
    setNewSeason("");
    setNewShortDesc("");
    setNewHeroImageUrl("");
    setNewFeaturedImageUrl("");
    setNewStoryTitle("");
    setNewStoryBody("");
  };

  const createCollection = async () => {
    if (!newName.trim()) return showToast("Tên không được để trống", "warning");
    const payload = {
      name: newName.trim(),
      slug: (newSlug || toSlug(newName)).trim(),
      description: newDescription?.trim() || null,
      shortDesc: newShortDesc?.trim() || null,
      season: newSeason?.trim() || null,
      heroImageUrl: newHeroImageUrl?.trim() || null,
      featuredImageUrl: newFeaturedImageUrl?.trim() || null,
      storyTitle: newStoryTitle?.trim() || null,
      storyBody: newStoryBody?.trim() || null,
      isActive: Boolean(newActive),
      sortOrder:
        newSortOrder === "" || newSortOrder === null
          ? null
          : Number(newSortOrder),
      era: newEra,
      kind: "DROP", // ✅ THÊM DÒNG NÀY
    };

    try {
      setCreating(true);
      const res = await axios.post(`${config.apiBaseUrl}/collections`, payload);
      const newId = res?.data?.id;
      const newItem = { id: newId, ...payload };
      setCollections((prev) => [newItem, ...prev]);
      if (newId) setCollectionId(newId);
      setOpenCreate(false);
      resetForm();
      showToast("Đã tạo bộ sưu tập mới.");
    } catch (e) {
      showToast(
        e?.response?.data?.message || "Tạo bộ sưu tập thất bại.",
        "error",
      );
    } finally {
      setCreating(false);
    }
  };

  const saveCollection = async () => {
    if (!newName.trim()) return showToast("Tên không được để trống", "warning");
    const payload = {
      name: newName.trim(),
      slug: (newSlug || toSlug(newName)).trim(),
      description: newDescription?.trim() || null,
      isActive: Boolean(newActive),
      sortOrder:
        newSortOrder === "" || newSortOrder === null
          ? null
          : Number(newSortOrder),
      era: newEra,
      kind: "DROP", // ✅ THÊM DÒNG NÀY
    };

    try {
      setCreating(true);
      if (editing) {
        await axios.put(
          `${config.apiBaseUrl}/collections/${editing.id}`,
          payload,
        );
        setCollections((prev) =>
          prev.map((c) =>
            (c.id ?? c.collectionId) === editing.id ? { ...c, ...payload } : c,
          ),
        );
        showToast("Đã cập nhật bộ sưu tập.");
      } else {
        const res = await axios.post(
          `${config.apiBaseUrl}/collections`,
          payload,
        );
        const newId = res?.data?.id;
        setCollections((prev) => [{ id: newId, ...payload }, ...prev]);
        if (newId) setCollectionId(newId);
        showToast("Đã tạo bộ sưu tập mới.");
      }
      setOpenForm(false);
      setEditing(null);
      resetForm();
    } catch (e) {
      showToast(e?.response?.data?.message || "Lưu thất bại.", "error");
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteCollection = async () => {
    if (!collectionId) return;
    if (!window.confirm("Bạn có chắc chắn muốn xoá bộ sưu tập này?")) return;
    try {
      await axios.delete(`${config.apiBaseUrl}/collections/${collectionId}`);
      setCollections((prev) =>
        prev.filter((c) => (c.id ?? c.collectionId) !== collectionId),
      );
      setCollectionId("");
      setInCollection([]);
      showToast("Đã xoá bộ sưu tập.");
    } catch (e) {
      showToast(e?.response?.data?.message || "Xoá thất bại.", "error");
    }
  };

  // HEIGHT
  const TABLE_H = { xs: 360, sm: 480, md: "calc(100vh - 240px)" };

  // ✅ FIX sticky header + scroll tràn
  const tableSx = {
    "& .MuiTableCell-root": {
      py: 1,
      px: 1,
      fontSize: "0.9rem",
      borderBottom: (t) => `1px solid ${t.palette.divider}`,
    },
    "& .MuiTableHead-root .MuiTableCell-root": {
      position: "sticky",
      top: 0,
      zIndex: 5,
      fontSize: "0.78rem",
      fontWeight: 800,
      textTransform: "uppercase",
      bgcolor: "background.paper",
      boxShadow: (t) => `0 1px 0 ${t.palette.divider}`,
    },
  };

  const panelSx = {
    borderRadius: 2.5,
    border: (t) => `1px solid ${t.palette.divider}`,
    overflow: "hidden",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    bgcolor: "background.paper",
  };

  return (
    <Box
      sx={{
        p: { xs: 1.5, md: 2 },
        width: "100%",
        minWidth: 0,
        height: "calc(100vh - 64px)",
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        overflow: "hidden",
      }}
    >
      {/* Top bar */}
      <Paper
        elevation={0}
        sx={{
          p: 1.25,
          borderRadius: 2.5,
          border: (t) => `1px solid ${t.palette.divider}`,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 1,
          alignItems: { xs: "stretch", md: "center" },
          justifyContent: "space-between",
        }}
      >
        <Stack spacing={0.25}>
          <Typography variant={smDown ? "subtitle1" : "h6"} fontWeight={900}>
            Sản phẩm trong bộ sưu tập
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Chọn bộ sưu tập → chọn sản phẩm → thêm vào → kéo thả sắp xếp & lưu
          </Typography>
        </Stack>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={onlyDrop}
                onChange={(e) => {
                  setOnlyDrop(e.target.checked);
                  setCollectionId("");
                  setInCollection([]);
                }}
              />
            }
            label="Chỉ Drop"
          />

          <TextField
            select
            size="small"
            label="Era"
            value={filterEra}
            onChange={(e) => {
              setFilterEra(e.target.value);
              setCollectionId("");
              setInCollection([]);
            }}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="new">new</MenuItem>
            <MenuItem value="old">old</MenuItem>
            <MenuItem value="all">all</MenuItem>
          </TextField>

          <TextField
            select
            size="small"
            label="Active"
            value={String(filterActive)}
            onChange={(e) => {
              setFilterActive(e.target.value === "true");
              setCollectionId("");
              setInCollection([]);
            }}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="true">Đang mở</MenuItem>
            <MenuItem value="false">Đã đóng</MenuItem>
            <MenuItem value="all">Tất cả</MenuItem>
          </TextField>

          <TextField
            select
            size="small"
            label="Bộ sưu tập"
            value={collectionId}
            onChange={(e) => {
              setCollectionId(e.target.value);
              setSelectedLeftIds(new Set());
              setSearchIn("");
            }}
            sx={{ minWidth: { xs: "100%", sm: 320 } }}
            fullWidth={smDown}
          >
            {collections.map((c) => (
              <MenuItem
                key={c.id ?? c.collectionId}
                value={c.id ?? c.collectionId}
              >
                {c.name} {c.era ? `(${c.era})` : ""}
              </MenuItem>
            ))}
          </TextField>

          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button
              variant="outlined"
              size="small"
              startIcon={<Add />}
              onClick={() => setOpenCreate(true)}
            >
              Tạo
            </Button>

            <Button
              variant="outlined"
              size="small"
              startIcon={<Edit />}
              disabled={!collectionId}
              onClick={() => {
                const col = collections.find(
                  (c) => (c.id ?? c.collectionId) === collectionId,
                );
                if (!col) return;
                setEditing(col);
                setNewName(col.name);
                setNewSlug(col.slug);
                setNewDescription(col.description ?? "");
                setNewShortDesc(col.shortDesc ?? "");
                setNewSeason(col.season ?? "");
                setNewHeroImageUrl(col.heroImageUrl ?? "");
                setNewFeaturedImageUrl(col.featuredImageUrl ?? "");
                setNewStoryTitle(col.storyTitle ?? "");
                setNewStoryBody(col.storyBody ?? "");
                setNewEra(col.era ?? "new");
                setNewActive(Boolean(col.isActive));
                setNewSortOrder(col.sortOrder ?? "");
                setOpenForm(true);
              }}
            >
              Sửa
            </Button>

            <Button
              variant="outlined"
              size="small"
              disabled={!collectionId}
              onClick={() => {
                const col = collections.find(
                  (c) => (c.id ?? c.collectionId) === collectionId,
                );
                if (!col?.slug)
                  return showToast("Drop chưa có slug.", "warning");
                const eraParam = col.era || filterEra || "new";
                window.open(
                  `/drops?era=${encodeURIComponent(eraParam)}&drop=${encodeURIComponent(col.slug)}`,
                  "_blank",
                );
              }}
            >
              Preview
            </Button>

            <Button
              variant="outlined"
              size="small"
              color="error"
              startIcon={<Delete />}
              disabled={!collectionId}
              onClick={handleDeleteCollection}
            >
              Xoá
            </Button>

            <Button
              variant="contained"
              size="small"
              startIcon={<Add />}
              disabled={!collectionId || selectedCount === 0}
              onClick={handleAddMany}
            >
              Thêm đã chọn {selectedCount ? `(${selectedCount})` : ""}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {!!error && (
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Two panels */}
      <Grid
        container
        spacing={{ xs: 1.5, md: 2 }}
        sx={{ flex: 1, minHeight: 0 }}
      >
        {/* LEFT */}
        <Grid item xs={12} md={6} sx={{ minHeight: 0, display: "flex" }}>
          <Box sx={{ ...panelSx, width: "100%" }}>
            <Box sx={{ p: 1.25 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ minWidth: 0 }}
                >
                  <Typography fontWeight={800}>Tất cả sản phẩm</Typography>
                  {selectedCount > 0 && (
                    <Chip size="small" label={`Đã chọn: ${selectedCount}`} />
                  )}
                </Stack>

                <Button
                  size="small"
                  variant="text"
                  onClick={() => setSelectedLeftIds(new Set())}
                  disabled={selectedCount === 0}
                >
                  Bỏ chọn
                </Button>
              </Stack>

              <Box sx={{ mt: 1 }}>
                <TextField
                  value={searchAll}
                  onChange={(e) => setSearchAll(e.target.value)}
                  size="small"
                  placeholder="Tìm theo tên hoặc SKU..."
                  fullWidth
                  InputProps={{
                    endAdornment: searchAll ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setSearchAll("")}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
              </Box>
            </Box>

            <Divider />

            {loadingLeft ? (
              <Box
                sx={{ flex: 1, display: "grid", placeItems: "center", p: 3 }}
              >
                <Stack alignItems="center" spacing={1}>
                  <CircularProgress />
                  <Typography variant="body2" color="text.secondary">
                    Đang tải sản phẩm...
                  </Typography>
                </Stack>
              </Box>
            ) : filteredAll.length === 0 ? (
              <Box
                sx={{ flex: 1, display: "grid", placeItems: "center", p: 3 }}
              >
                <Stack spacing={0.5} alignItems="center">
                  <Typography fontWeight={700}>
                    Không có sản phẩm phù hợp
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Thử đổi từ khoá tìm kiếm
                  </Typography>
                </Stack>
              </Box>
            ) : (
              <TableContainer
                component={Box}
                sx={{
                  position: "relative",
                  isolation: "isolate",
                  flex: 1,
                  minHeight: 0,
                  maxHeight: { xs: TABLE_H.xs, sm: TABLE_H.sm, md: TABLE_H.md },
                  overflow: "auto",
                  ...tableSx,
                }}
              >
                <Table
                  stickyHeader
                  size="small"
                  sx={{
                    tableLayout: "fixed",
                    borderCollapse: "separate",
                    borderSpacing: 0,
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 44 }}>
                        <Checkbox
                          size="small"
                          indeterminate={selectedSome}
                          checked={selectedAll}
                          onChange={(e) =>
                            toggleLeftAllFiltered(e.target.checked)
                          }
                        />
                      </TableCell>
                      <TableCell sx={{ ...hideXs, width: { sm: 86 } }}>
                        Mã
                      </TableCell>
                      <TableCell>Tên sản phẩm</TableCell>
                      <TableCell sx={{ ...hideXs, width: { sm: 120 } }}>
                        Tồn
                      </TableCell>
                      <TableCell align="right" sx={{ width: 64 }}>
                        +
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {filteredAll.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>
                          <Checkbox
                            size="small"
                            checked={selectedLeftIds.has(row.id)}
                            onChange={(e) =>
                              toggleLeft(row.id, e.target.checked)
                            }
                          />
                        </TableCell>

                        <TableCell sx={hideXs}>{row.id}</TableCell>

                        <TableCell sx={ellipsisCell} title={row.name}>
                          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              fontWeight={700}
                              sx={ellipsisCell}
                            >
                              {row.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={ellipsisCell}
                            >
                              SKU: {row.sku || "-"} • {row.priceText}
                            </Typography>
                          </Stack>
                        </TableCell>

                        <TableCell sx={hideXs}>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`${row.totalStock} • ${row.stockStatus}`}
                          />
                        </TableCell>

                        <TableCell align="right">
                          <Tooltip title="Thêm vào bộ sưu tập">
                            <span>
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() => handleAddOne(row)}
                              >
                                <Add />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Grid>

        {/* RIGHT */}
        <Grid item xs={12} md={6} sx={{ minHeight: 0, display: "flex" }}>
          <Box sx={{ ...panelSx, width: "100%" }}>
            <Box sx={{ p: 1.25 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
              >
                <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                  <Typography fontWeight={800} sx={ellipsisCell}>
                    Trong bộ sưu tập {collectionId ? `#${collectionId}` : ""}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {searchIn
                      ? "Đang tìm kiếm — tắt tìm kiếm để kéo thả sắp xếp"
                      : "Kéo thả bằng biểu tượng ⋮⋮ để sắp xếp"}
                  </Typography>
                </Stack>

                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Save />}
                  onClick={saveReorder}
                  disabled={!collectionId || inCollection.length === 0}
                >
                  Lưu sắp xếp
                </Button>
              </Stack>

              <Box sx={{ mt: 1 }}>
                <TextField
                  value={searchIn}
                  onChange={(e) => setSearchIn(e.target.value)}
                  size="small"
                  placeholder="Tìm trong bộ sưu tập..."
                  fullWidth
                  InputProps={{
                    endAdornment: searchIn ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setSearchIn("")}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
              </Box>
            </Box>

            <Divider />

            {loadingRight ? (
              <Box
                sx={{ flex: 1, display: "grid", placeItems: "center", p: 3 }}
              >
                <Stack alignItems="center" spacing={1}>
                  <CircularProgress />
                  <Typography variant="body2" color="text.secondary">
                    Đang tải bộ sưu tập...
                  </Typography>
                </Stack>
              </Box>
            ) : filteredIn.length === 0 ? (
              <Box
                sx={{ flex: 1, display: "grid", placeItems: "center", p: 3 }}
              >
                <Stack spacing={0.5} alignItems="center">
                  <Typography fontWeight={700}>Chưa có sản phẩm</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Chọn sản phẩm bên trái và bấm “Thêm”
                  </Typography>
                </Stack>
              </Box>
            ) : (
              <TableContainer
                component={Box}
                sx={{
                  position: "relative",
                  isolation: "isolate",
                  flex: 1,
                  minHeight: 0,
                  maxHeight: { xs: TABLE_H.xs, sm: TABLE_H.sm, md: TABLE_H.md },
                  overflow: "auto",
                  ...tableSx,
                }}
              >
                <Table
                  stickyHeader
                  size="small"
                  sx={{
                    tableLayout: "fixed",
                    borderCollapse: "separate",
                    borderSpacing: 0,
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 44 }} />
                      <TableCell sx={{ width: 56 }}>#</TableCell>
                      <TableCell sx={{ ...hideXs, width: { sm: 86 } }}>
                        Mã
                      </TableCell>
                      <TableCell>Tên</TableCell>
                      <TableCell sx={{ width: 72 }}>Xoá</TableCell>
                    </TableRow>
                  </TableHead>

                  {/* ✅ DND CONTEXT */}
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={
                        searchIn
                          ? filteredIn.map((x) => x.id)
                          : inCollection.map((x) => x.id)
                      }
                      strategy={verticalListSortingStrategy}
                    >
                      <TableBody>
                        {(searchIn ? filteredIn : inCollection).map((row) => (
                          <SortableRow key={row.id} row={row}>
                            <TableCell>
                              <Chip size="small" label={row.sortOrder} />
                            </TableCell>

                            <TableCell sx={hideXs}>{row.id}</TableCell>

                            <TableCell sx={ellipsisCell} title={row.name}>
                              <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                                <Typography
                                  variant="body2"
                                  fontWeight={700}
                                  sx={ellipsisCell}
                                >
                                  {row.name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={ellipsisCell}
                                >
                                  SKU: {row.sku || "-"} • {row.priceText}
                                </Typography>
                              </Stack>
                            </TableCell>

                            <TableCell>
                              <Tooltip title="Xoá khỏi bộ sưu tập">
                                <IconButton
                                  color="error"
                                  size="small"
                                  onClick={() =>
                                    handleRemoveFromCollection(row)
                                  }
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </SortableRow>
                        ))}
                      </TableBody>
                    </SortableContext>
                  </DndContext>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={2200}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.sev}
          sx={{ width: "100%" }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>

      {/* Dialog tạo BST */}
      <Dialog
        open={openCreate}
        onClose={() => !creating && setOpenCreate(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Tạo bộ sưu tập</DialogTitle>
        <DialogContent
          sx={{ pt: 1, display: "flex", flexDirection: "column", gap: 2 }}
        >
          <TextField
            autoFocus
            label="Tên"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            size="small"
            fullWidth
          />
          <TextField
            label="Slug"
            value={newSlug}
            onChange={(e) => setNewSlug(toSlug(e.target.value))}
            helperText="Tự tạo từ tên, có thể chỉnh"
            size="small"
            fullWidth
          />
          <TextField
            label="Mô tả"
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={2}
          />
          <TextField
            label="Season (ví dụ: 02:17 AM)"
            value={newSeason}
            onChange={(e) => setNewSeason(e.target.value)}
            size="small"
            fullWidth
          />

          <TextField
            label="Short desc (mô tả ngắn)"
            value={newShortDesc}
            onChange={(e) => setNewShortDesc(e.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={2}
          />

          <TextField
            label="Hero image URL"
            value={newHeroImageUrl}
            onChange={(e) => setNewHeroImageUrl(e.target.value)}
            size="small"
            fullWidth
          />

          <TextField
            label="Featured image URL"
            value={newFeaturedImageUrl}
            onChange={(e) => setNewFeaturedImageUrl(e.target.value)}
            size="small"
            fullWidth
          />

          <TextField
            label="Story title"
            value={newStoryTitle}
            onChange={(e) => setNewStoryTitle(e.target.value)}
            size="small"
            fullWidth
          />

          <TextField
            label="Story body"
            value={newStoryBody}
            onChange={(e) => setNewStoryBody(e.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={4}
          />

          <TextField
            select
            label="Era"
            size="small"
            value={newEra}
            onChange={(e) => setNewEra(e.target.value)}
          >
            <MenuItem value="all">all</MenuItem>
            <MenuItem value="new">new</MenuItem>
            <MenuItem value="old">old</MenuItem>
          </TextField>
          <TextField
            type="number"
            label="Sort order"
            size="small"
            value={newSortOrder}
            onChange={(e) => setNewSortOrder(e.target.value)}
            inputProps={{ min: 0, step: 1 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={newActive}
                onChange={(e) => setNewActive(e.target.checked)}
              />
            }
            label="Kích hoạt (IsActive)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreate(false)} disabled={creating}>
            Huỷ
          </Button>
          <Button
            variant="contained"
            onClick={createCollection}
            disabled={creating}
          >
            {creating ? "Đang tạo..." : "Tạo"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog sửa */}
      <Dialog
        open={openForm}
        onClose={() => !creating && setOpenForm(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editing ? "Sửa bộ sưu tập" : "Tạo bộ sưu tập"}
        </DialogTitle>
        <DialogContent
          sx={{ pt: 1, display: "flex", flexDirection: "column", gap: 2 }}
        >
          <TextField
            label="Tên"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <TextField
            label="Slug"
            value={newSlug}
            onChange={(e) => setNewSlug(toSlug(e.target.value))}
          />
          <TextField
            label="Mô tả"
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            multiline
          />
          <TextField
            label="Season (ví dụ: 02:17 AM)"
            value={newSeason}
            onChange={(e) => setNewSeason(e.target.value)}
            size="small"
            fullWidth
          />

          <TextField
            label="Short desc (mô tả ngắn)"
            value={newShortDesc}
            onChange={(e) => setNewShortDesc(e.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={2}
          />

          <TextField
            label="Hero image URL"
            value={newHeroImageUrl}
            onChange={(e) => setNewHeroImageUrl(e.target.value)}
            size="small"
            fullWidth
          />

          <TextField
            label="Featured image URL"
            value={newFeaturedImageUrl}
            onChange={(e) => setNewFeaturedImageUrl(e.target.value)}
            size="small"
            fullWidth
          />

          <TextField
            label="Story title"
            value={newStoryTitle}
            onChange={(e) => setNewStoryTitle(e.target.value)}
            size="small"
            fullWidth
          />

          <TextField
            label="Story body"
            value={newStoryBody}
            onChange={(e) => setNewStoryBody(e.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={4}
          />

          <TextField
            select
            label="Era"
            value={newEra}
            onChange={(e) => setNewEra(e.target.value)}
          >
            <MenuItem value="new">new</MenuItem>
            <MenuItem value="old">old</MenuItem>
          </TextField>
          <TextField
            type="number"
            label="Sort order"
            value={newSortOrder}
            onChange={(e) => setNewSortOrder(e.target.value)}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={newActive}
                onChange={(e) => setNewActive(e.target.checked)}
              />
            }
            label="Kích hoạt"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenForm(false)}>Huỷ</Button>
          <Button
            variant="contained"
            onClick={saveCollection}
            disabled={creating}
          >
            {creating ? "Đang lưu..." : editing ? "Lưu" : "Tạo"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
